import * as React from 'react';
import { useIntl } from 'react-intl';
import type { ShowellApi } from '@showell-labs/api-client';
import { browseMessages } from '../../intl/messages';
import type {
  BrowseItem,
  BreadcrumbEntry,
  FolderItem,
  FileItem,
  RawItem,
} from './types';

interface UseBrowseListingResult {
  rawItems: BrowseItem[];
  loading: boolean;
  error: string | null;
  breadcrumbs: BreadcrumbEntry[];
  currentPath: string | undefined;
  openFolder: (folder: FolderItem) => void;
  navigateToBreadcrumb: (index: number) => void;
  clearSearch: () => void;
}

interface UseBrowseListingOptions {
  api: ShowellApi | null;
  rootPath: string | null;
  onClearSearch: () => void;
}

export function useBrowseListing({
  api,
  rootPath,
  onClearSearch,
}: UseBrowseListingOptions): UseBrowseListingResult {
  const intl = useIntl();
  const allFilesLabel = intl.formatMessage(browseMessages.allFiles);

  const [breadcrumbs, setBreadcrumbs] = React.useState<BreadcrumbEntry[]>(() =>
    rootPath ? [{ path: rootPath, name: allFilesLabel }] : []
  );
  const [rawItems, setRawItems] = React.useState<BrowseItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const currentPath = breadcrumbs[breadcrumbs.length - 1]?.path;

  React.useEffect(() => {
    if (rootPath) {
      setBreadcrumbs([{ path: rootPath, name: allFilesLabel }]);
      setError(null);
    }
  }, [rootPath, allFilesLabel]);

  React.useEffect(() => {
    if (!api || !currentPath) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const directoryClient = api.client('directory');
        const result = (await directoryClient.listDir({
          query: currentPath,
          specs: {
            id: true,
            name: true,
            type: true,
            path: true,
            externalRef: true,
            signedUrl: true,
            signedImageUrls: { default: true },
            original: { extension: true, signedUrl: true },
            meta: { title: true },
          },
        })) as RawItem[];

        if (cancelled) {
          return;
        }

        const folderItems: FolderItem[] = [];
        const fileItems: FileItem[] = [];

        for (const item of result) {
          if (!item.path) {
            continue;
          }

          if (item.type === 'directory') {
            if (item.name.startsWith('.')) {
              continue;
            }
            folderItems.push({
              id: item.id,
              name: item.name,
              type: 'directory',
              path: item.path,
            });
            continue;
          }

          const ext = item.original?.extension?.toLowerCase();
          if (ext === 'pptx' || ext === 'ppt') {
            fileItems.push({
              id: item.id,
              name: item.name,
              title: item.meta?.title || item.name,
              type: 'file',
              path: item.path,
              signedUrl: item.signedUrl,
              originalSignedUrl: item.original?.signedUrl ?? undefined,
              externalRef: item.externalRef,
              thumbnailUrl: item.signedImageUrls?.default ?? undefined,
            });
          }
        }

        folderItems.sort((a, b) => a.name.localeCompare(b.name));
        fileItems.sort((a, b) => a.name.localeCompare(b.name));
        setRawItems([...folderItems, ...fileItems]);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setError(
          e instanceof Error
            ? e.message
            : intl.formatMessage(browseMessages.failedToLoad)
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, currentPath, intl]);

  function openFolder(folder: FolderItem) {
    setBreadcrumbs(prev => [...prev, { path: folder.path, name: folder.name }]);
    onClearSearch();
  }

  function navigateToBreadcrumb(index: number) {
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    onClearSearch();
  }

  return {
    rawItems,
    loading,
    error,
    breadcrumbs,
    currentPath,
    openFolder,
    navigateToBreadcrumb,
    clearSearch: onClearSearch,
  };
}
